import { FunctionComponent, useCallback, useMemo } from 'react';

import { useNotificationSocket, NotificationMessage } from '@components/common/custom_hooks/useNotificationSocket';

interface PeopleTaggingNotificationListenerProps {
  notificationsSocketIoServer: string | null;
}

/**
 * Extracts the brandfolder slug from a brandfolder URL.
 * URLs are in the format: https://brandfolder.com/slug
 */
const extractBrandfolderSlugFromUrl = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    // Get the last path segment (slug)
    return pathParts[pathParts.length - 1] || null;
  } catch {
    return null;
  }
};

const isPeopleTaggingBackfillReady = (
  message: NotificationMessage,
  brandfolderSlug: string
): boolean => {
  if (message?.notificationType !== 'people_tagging_backfill_ready') {
    return false;
  }

  // Extract brandfolder slug from the buttonText URL
  const notificationBrandfolderSlug = extractBrandfolderSlugFromUrl(message?.buttonText);
  return notificationBrandfolderSlug === brandfolderSlug;
};

/**
 * Invisible component that listens for people_tagging_backfill_ready notifications
 * and displays a SweetAlert when the backfill is complete for the current brandfolder.
 *
 * This component is mounted globally on all brandfolder/collection pages.
 */
export const PeopleTaggingNotificationListener: FunctionComponent<PeopleTaggingNotificationListenerProps> = ({
  notificationsSocketIoServer
}) => {
  const brandfolderSlug = useMemo(() => BFG.brandfolder_slug, []);

  const handleNotification = useCallback((message: NotificationMessage | string) => {
    // Only process object messages (not string messages from legacy notifications)
    if (typeof message !== 'object') {
      return;
    }

    if (isPeopleTaggingBackfillReady(message, brandfolderSlug)) {
      // Use the Notify system to show banner notification
      type NotifyWindow = typeof window & {
        Notify?: { create: (config: { type: string; title: string }) => void };
      };
      const notifyWindow = window as NotifyWindow;
      if (typeof notifyWindow.Notify !== 'undefined') {
        const combinedMessage = [message.title, message.body].filter(Boolean).join(' ');
        notifyWindow.Notify.create({
          type: 'success',
          title: combinedMessage
        });
      }
    }
  }, [brandfolderSlug]);

  useNotificationSocket({
    notificationsSocketIoServer,
    handlerKey: `people-tagging-backfill-${brandfolderSlug}`,
    onNotification: handleNotification
  });

  // Only register if we have a brandfolder slug (i.e., we're on a brandfolder/collection page)
  if (!brandfolderSlug) {
    return null;
  }

  // This component renders nothing - it's just a listener
  return null;
};

export default PeopleTaggingNotificationListener;
